import { z } from 'zod';
import { Channel } from '../enums';

export const MetricLevel = {
  ACCOUNT: 'ACCOUNT',
  CAMPAIGN: 'CAMPAIGN',
  AD_SET: 'AD_SET',
  AD: 'AD',
  KEYWORD: 'KEYWORD',
  GEO: 'GEO',
} as const;
export type MetricLevel = (typeof MetricLevel)[keyof typeof MetricLevel];

/** Métricas normalizadas — mesmo formato para Meta e Google. */
export const metricValuesSchema = z.object({
  impressions: z.number().int().min(0).default(0),
  reach: z.number().int().min(0).default(0),
  clicks: z.number().int().min(0).default(0),
  spendCents: z.number().int().min(0).default(0),
  conversions: z.number().min(0).default(0),
  leads: z.number().min(0).default(0),
  messagingConversations: z.number().min(0).default(0),
  calls: z.number().min(0).default(0),
  formSubmissions: z.number().min(0).default(0),
  videoViews: z.number().min(0).default(0),
  frequency: z.number().min(0).default(0),
});
export type MetricValues = z.infer<typeof metricValuesSchema>;

export interface DerivedMetrics {
  ctr: number;
  cpcCents: number;
  cpmCents: number;
  cplCents: number;
  conversionRate: number;
  costPerConversionCents: number;
}

export function deriveMetrics(m: MetricValues): DerivedMetrics {
  const safeDiv = (a: number, b: number) => (b > 0 ? a / b : 0);
  return {
    ctr: safeDiv(m.clicks, m.impressions) * 100,
    cpcCents: Math.round(safeDiv(m.spendCents, m.clicks)),
    cpmCents: Math.round(safeDiv(m.spendCents, m.impressions) * 1000),
    cplCents: Math.round(safeDiv(m.spendCents, m.leads)),
    conversionRate: safeDiv(m.conversions, m.clicks) * 100,
    costPerConversionCents: Math.round(safeDiv(m.spendCents, m.conversions)),
  };
}

export function sumMetrics(list: MetricValues[]): MetricValues {
  const base: MetricValues = {
    impressions: 0,
    reach: 0,
    clicks: 0,
    spendCents: 0,
    conversions: 0,
    leads: 0,
    messagingConversations: 0,
    calls: 0,
    formSubmissions: 0,
    videoViews: 0,
    frequency: 0,
  };
  for (const m of list) {
    base.impressions += m.impressions;
    base.reach += m.reach;
    base.clicks += m.clicks;
    base.spendCents += m.spendCents;
    base.conversions += m.conversions;
    base.leads += m.leads;
    base.messagingConversations += m.messagingConversations;
    base.calls += m.calls;
    base.formSubmissions += m.formSubmissions;
    base.videoViews += m.videoViews;
  }
  base.frequency = base.reach > 0 ? base.impressions / base.reach : 0;
  return base;
}

export const metricsQuerySchema = z.object({
  clientId: z.string().uuid().optional(),
  channel: z.nativeEnum(Channel).optional(),
  level: z.nativeEnum(MetricLevel).default('CAMPAIGN'),
  from: z.coerce.date(),
  to: z.coerce.date(),
  campaignIds: z.array(z.string().uuid()).optional(),
  groupBy: z.enum(['DAY', 'WEEK', 'MONTH', 'CITY', 'PLAN', 'CREATIVE', 'KEYWORD']).default('DAY'),
  compareToPreviousPeriod: z.boolean().default(false),
});
export type MetricsQuery = z.infer<typeof metricsQuerySchema>;

export interface MetricSeriesPoint {
  bucket: string;
  values: MetricValues;
  derived: DerivedMetrics;
}

export interface MetricsResponse {
  totals: MetricValues;
  derived: DerivedMetrics;
  series: MetricSeriesPoint[];
  previous?: { totals: MetricValues; derived: DerivedMetrics };
}
